import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Table } from 'react-bootstrap';
import { FaArrowLeft, FaEdit, FaLink, FaPlus, FaTrashAlt, FaFilePdf, FaClipboardList, FaTable } from 'react-icons/fa';
import H1 from '../Images/Hma.jpeg';
import L1 from '../Images/logo-w.png';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import 'bootstrap/dist/css/bootstrap.min.css';

const SesionFeedback = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [sessionTopic, setSessionTopic] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [sessions, setSessions] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [editingSessionId, setEditingSessionId] = useState(null);
    const itemsPerPage = 5;

    // Feedback Data & Pagination states (20 per page)
    const [feedbackData, setFeedbackData] = useState([]);
    const [feedbackPage, setFeedbackPage] = useState(1);
    const feedbackItemsPerPage = 20;

    const navigate = useNavigate();

    useEffect(() => {
        fetchSessions();
        fetchFeedbackData();
    }, []);

    const fetchSessions = async () => {
        try {
            const response = await fetch('https://rvapi.emedha.in/pages/api/fdgsessions');
            const data = await response.json();

            if (Array.isArray(data)) {
                setSessions(data);
            } else if (data?.sessions && Array.isArray(data.sessions)) {
                setSessions(data.sessions);
            } else {
                setSessions([]);
            }
        } catch (error) {
            console.error("Error fetching sessions:", error);
            setSessions([]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const method = editingSessionId ? 'PUT' : 'POST';
        const url = editingSessionId
            ? `https://rvapi.emedha.in/pages/api/fdesessions/${editingSessionId}`
            : 'https://rvapi.emedha.in/pages/api/fdpsessions';

        try {
            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sessionTopic, startDate, endDate }),
            });

            const data = await response.json();
            if (method === 'POST') {
                setSessions([...sessions, data]);
                alert('Session added successfully!');
            } else {
                setSessions(
                    sessions.map((session) =>
                        session.id === editingSessionId ? data : session
                    )
                );
                alert('Session updated successfully!');
            }

            setIsModalOpen(false);
            setSessionTopic('');
            setStartDate('');
            setEndDate('');
            setEditingSessionId(null);
        } catch (error) {
            console.error("Error submitting session:", error);
            alert('Error saving session');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this session?")) return;
        try {
            await fetch(`https://rvapi.emedha.in/pages/api/fddsessions/${id}`, {
                method: 'DELETE',
            });
            setSessions(sessions.filter((session) => session.id !== id));
            alert('Session deleted successfully!');
        } catch (error) {
            console.error("Error deleting session:", error);
        }
    };

    const handleEdit = (session) => {
        setSessionTopic(session.sessionTopic);
        setStartDate(session.startDate);
        setEndDate(session.endDate);
        setEditingSessionId(session.id);
        setIsModalOpen(true);
    };

    const handlePagination = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleFeedbackPagination = (pageNumber) => {
        setFeedbackPage(pageNumber);
    };

    const indexOfLastSession = currentPage * itemsPerPage;
    const indexOfFirstSession = indexOfLastSession - itemsPerPage;
    const currentSessions = sessions.slice(indexOfFirstSession, indexOfLastSession);

    const indexOfLastFeedback = feedbackPage * feedbackItemsPerPage;
    const indexOfFirstFeedback = indexOfLastFeedback - feedbackItemsPerPage;
    const currentFeedbackData = feedbackData.slice(indexOfFirstFeedback, indexOfLastFeedback);

    const handleGenerateUrl = (session) => {
        navigate(`/FeedbackForm/${session.id}/feedbackForm-in-feedbackForm`);
    };

    const fetchFeedbackData = () => {
        fetch('https://rvapi.emedha.in/pages/api/feedbackformdata')
            .then(response => response.json())
            .then(data => setFeedbackData(data))
            .catch(error => console.error('Error fetching data:', error));
    };

    function maskEmail(email) {
        if (!email) return '';
        const [name, domain] = email.split('@');
        if (!name || !domain) return email;
        const maskedName = name[0] + '*'.repeat(name.length - 1);
        return `${maskedName}@${domain}`;
    }

    const exportPDF = () => {
        const doc = new jsPDF();
        doc.setFontSize(8);
        doc.text("Feedback Form Data", 10, 15);

        const headers = [
            "No", "Name", "Mobile", "Email", "Liked Topic", "Liked Topics?",
            "Speakers Expectation?", "Attend Again?", "Feedback", "Submitted At"
        ];

        const columnWidths = [5, 20, 20, 30, 30, 12, 20, 15, 30, 20];
        const startX = 10;
        let startY = 30;

        doc.setFont(undefined, 'bold');
        let x = startX;
        headers.forEach((header, i) => {
            doc.text(header, x, startY, { maxWidth: columnWidths[i] });
            x += columnWidths[i];
        });

        doc.setFont(undefined, 'normal');
        startY += 10;

        feedbackData.forEach((item, index) => {
            const mobile = item.mobile
                ? item.mobile.replace(/\d(?=\d{4})/g, 'x')
                : '';

            const email = item.email ? maskEmail(item.email) : '';

            const row = [
                index + 1,
                item.name,
                mobile,
                email,
                item.liked_topic,
                item.liked_topics,
                item.speakers_expectation,
                item.attend_future_conferences,
                item.overall_feedback,
                new Date(item.created_at).toLocaleString()
            ];

            let x = startX;
            let rowHeights = [];

            row.forEach((text, i) => {
                let cellText = String(text || '');
                const lines = doc.splitTextToSize(cellText, columnWidths[i]);
                doc.text(lines, x, startY);
                rowHeights.push(lines.length);
                x += columnWidths[i];
            });

            const maxLines = Math.max(...rowHeights);
            const rowHeight = maxLines * 5;

            startY += rowHeight;

            if (startY > 270) {
                doc.addPage();
                startY = 20;
            }
        });

        doc.save("feedback_form_data.pdf");
    };

    return (
        <div className="session-feedback-page">

            {/* ================= HERO SECTION ================= */}
            <section className="hero-section">
                <img src={H1} alt="EMedha" className="hero-image" />
                <div className="hero-overlay">
                    <div className="hero-content">
                        <span className="hero-label">Management</span>
                        <h1>Session & Feedback Dashboard</h1>
                        <p>Manage conference sessions and review attendee evaluations</p>
                    </div>
                </div>
            </section>

            {/* ================= MAIN CONTAINER ================= */}
            <main className="main-content">
                <div className="container">

                    {/* Top Action Controls */}
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
                        <button className="btn btn-outline-secondary back-btn" onClick={() => window.history.back()}>
                            <FaArrowLeft /> Back
                        </button>
                        <button className="btn btn-success add-btn" onClick={() => { setIsModalOpen(true); setEditingSessionId(null); setSessionTopic(''); setStartDate(''); setEndDate(''); }}>
                            <FaPlus /> Add Session For Feedback
                        </button>
                    </div>

                    {/* Sessions Section Card */}
                    <div className="dashboard-card mb-5">
                        <div className="card-header-custom">
                            <div className="d-flex align-items-center gap-2">
                                <FaClipboardList className="header-icon text-primary" />
                                <h3>Managed Sessions</h3>
                            </div>
                            <span className="badge bg-primary rounded-pill">{sessions.length} Sessions</span>
                        </div>

                        <div className="table-responsive">
                            <Table hover className="align-middle mb-0 custom-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Session Topic</th>
                                        <th>Start Date & Time</th>
                                        <th>End Date & Time</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentSessions.length > 0 ? (
                                        currentSessions.map((session, index) => (
                                            <tr key={session.id}>
                                                <td>{indexOfFirstSession + index + 1}</td>
                                                <td className="fw-semibold text-dark">{session.sessionTopic}</td>
                                                <td>{new Date(session.startDate).toLocaleString()}</td>
                                                <td>{new Date(session.endDate).toLocaleString()}</td>
                                                <td className="text-end">
                                                    <div className="d-flex justify-content-end gap-2">
                                                        <button className="btn btn-warning btn-sm action-btn" onClick={() => handleEdit(session)} title="Edit">
                                                            <FaEdit /> <span className="d-none d-md-inline">Edit</span>
                                                        </button>
                                                        <button className="btn btn-danger btn-sm action-btn" onClick={() => handleDelete(session.id)} title="Delete">
                                                            <FaTrashAlt /> <span className="d-none d-md-inline">Delete</span>
                                                        </button>
                                                        <button className="btn btn-info btn-sm action-btn text-white" onClick={() => handleGenerateUrl(session)} title="Get URL">
                                                            <FaLink /> <span className="d-none d-md-inline">URL</span>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center py-4 text-muted">No sessions available.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        {/* Pagination */}
                        {sessions.length > itemsPerPage && (
                            <div className="d-flex justify-content-center align-items-center p-3 pagination-container gap-3">
                                <button onClick={() => handlePagination(currentPage - 1)} disabled={currentPage === 1} className="btn btn-outline-primary btn-sm">Previous</button>
                                <span className="text-muted fw-semibold">Page {currentPage} of {Math.ceil(sessions.length / itemsPerPage)}</span>
                                <button onClick={() => handlePagination(currentPage + 1)} disabled={currentPage * itemsPerPage >= sessions.length} className="btn btn-outline-primary btn-sm">Next</button>
                            </div>
                        )}
                    </div>

                    {/* Feedback Data Section Card */}
                    <div className="dashboard-card">
                        <div className="card-header-custom flex-wrap gap-2">
                            <div className="d-flex align-items-center gap-2">
                                <FaTable className="header-icon text-success" />
                                <h3>Feedback Form Data</h3>
                            </div>
                            <button className="btn btn-primary export-btn" onClick={exportPDF}>
                                <FaFilePdf /> Download PDF
                            </button>
                        </div>

                        <div className="table-responsive">
                            <Table hover className="align-middle mb-0 custom-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Name</th>
                                        <th>Mobile</th>
                                        <th>Email</th>
                                        <th>Liked Topic</th>
                                        <th>Liked Topics?</th>
                                        <th>Speakers Expectation?</th>
                                        <th>Attend Future Conferences?</th>
                                        <th>Overall Feedback</th>
                                        <th>Session Topic</th>
                                        <th>Submitted At</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentFeedbackData.length > 0 ? (
                                        currentFeedbackData.map((feedback, index) => (
                                            <tr key={feedback.id || index}>
                                                <td>{indexOfFirstFeedback + index + 1}</td>
                                                <td className="fw-semibold text-dark">{feedback.name}</td>
                                                <td>{feedback.mobile}</td>
                                                <td>{feedback.email}</td>
                                                <td>{feedback.liked_topic}</td>
                                                <td><span className={`badge ${feedback.liked_topics === 'Y' ? 'bg-success' : 'bg-secondary'}`}>{feedback.liked_topics}</span></td>
                                                <td><span className={`badge ${feedback.speakers_expectation === 'Y' ? 'bg-success' : 'bg-secondary'}`}>{feedback.speakers_expectation}</span></td>
                                                <td><span className={`badge ${feedback.attend_future_conferences === 'Y' ? 'bg-success' : 'bg-secondary'}`}>{feedback.attend_future_conferences}</span></td>
                                                <td>{feedback.session_topic}</td>
                                                <td>{feedback.overall_feedback}</td>
                                                <td>{new Date(feedback.created_at).toLocaleString()}</td>
                                            </tr>
                                        ))
                                    ) : ( 
                                        <tr>
                                            <td colSpan="10" className="text-center py-4 text-muted">No feedback records found.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        {/* Feedback Pagination (20 per page) */}
                        {feedbackData.length > feedbackItemsPerPage && (
                            <div className="d-flex justify-content-center align-items-center p-3 pagination-container gap-3">
                                <button onClick={() => handleFeedbackPagination(feedbackPage - 1)} disabled={feedbackPage === 1} className="btn btn-outline-success btn-sm">Previous</button>
                                <span className="text-muted fw-semibold">Page {feedbackPage} of {Math.ceil(feedbackData.length / feedbackItemsPerPage)}</span>
                                <button onClick={() => handleFeedbackPagination(feedbackPage + 1)} disabled={feedbackPage * feedbackItemsPerPage >= feedbackData.length} className="btn btn-outline-success btn-sm">Next</button>
                            </div>
                        )}
                    </div>

                </div>
            </main>

            {/* ================= MODAL ================= */}
            <Modal show={isModalOpen} onHide={() => setIsModalOpen(false)} centered size="lg" className="custom-modal">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold">{editingSessionId ? 'Edit Session Details' : 'Add New Session'}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3" controlId="formSessionTopic">
                            <Form.Label className="fw-semibold text-secondary">Session Topic</Form.Label>
                            <Form.Control type="text" value={sessionTopic} onChange={(e) => setSessionTopic(e.target.value)} placeholder="Enter session topic" required className="py-2" />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="formStartDate">
                            <Form.Label className="fw-semibold text-secondary">Start Date & Time</Form.Label>
                            <Form.Control type="datetime-local" value={startDate} onChange={(e) => setStartDate(e.target.value)} required className="py-2" />
                        </Form.Group>
                        <Form.Group className="mb-4" controlId="formEndDate">
                            <Form.Label className="fw-semibold text-secondary">End Date & Time</Form.Label>
                            <Form.Control type="datetime-local" value={endDate} onChange={(e) => setEndDate(e.target.value)} required className="py-2" />
                        </Form.Group>
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="light" onClick={() => setIsModalOpen(false)} className="px-4">Cancel</Button>
                            <Button type="submit" variant="success" className="px-4 fw-semibold">
                                {editingSessionId ? 'Update Session' : 'Save Session'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* ================= FOOTER ================= */}
            <footer className="footer-section">
                <div className="container">
                    <div className="footer-content">
                        <div className="footer-brand">
                            <a href="https://emedha.com/" target="_blank" rel="noopener noreferrer">
                                <img src={L1} alt="EMedha Technologies Logo" />
                            </a>
                        </div>
                        <div className="footer-text">
                            <p>Powered by{' '}
                                <a href="https://emedha.com/" target="_blank" rel="noopener noreferrer">EMedha Technologies</a>
                            </p>
                            <span>
                                © {new Date().getFullYear()} EMedha Technologies. All rights reserved.
                            </span>
                        </div>
                    </div>
                </div>
            </footer>

            {/* ================= STYLES ================= */}
            <style>{`
                * { box-sizing: border-box; }
                .session-feedback-page { min-height: 100vh; display: flex; flex-direction: column; background: #f8fafc; color: #1e293b; }
                
                /* HERO */
                .hero-section { position: relative; height: clamp(180px, 28vw, 260px); overflow: hidden; }
                .hero-image { width: 100%; height: 100%; object-fit: fit; display: block; }
                .hero-overlay { position: absolute; inset: 0; background: linear-gradient(90deg, rgba(15, 23, 42, 0.78), rgba(15, 23, 42, 0.3), rgba(15, 23, 42, 0.05)); display: flex; align-items: center; }
                .hero-content { width: 100%; max-width: 1200px; margin: auto; padding: 25px; color: white; }
                .hero-label { display: inline-block; padding: 5px 12px; border-radius: 20px; background: rgba(255, 255, 255, 0.18); backdrop-filter: blur(5px); font-size: 12px; margin-bottom: 8px; }
                .hero-content h1 { margin: 0; font-size: clamp(26px, 4vw, 38px); font-weight: 700; }
                .hero-content p { margin: 6px 0 0; font-size: clamp(13px, 2vw, 15px); color: rgba(255, 255, 255, 0.85); }

                /* MAIN */
                .main-content { flex: 1; padding: 35px 0 60px; }
                .dashboard-card { background: white; border: 1px solid #e2e8f0; border-radius: 16px; box-shadow: 0 4px 20px rgba(15, 23, 42, 0.05); overflow: hidden; margin-bottom: 30px; }
                .card-header-custom { padding: 20px 24px; border-bottom: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: space-between; background: #fff; }
                .card-header-custom h3 { margin: 0; font-size: 18px; font-weight: 700; color: #0f172a; }
                .header-icon { font-size: 20px; }

                /* TABLES */
                .custom-table th { background: #f8fafc !important; color: #475569 !important; font-weight: 600 !important; font-size: 13px; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #e2e8f0 !important; padding: 14px 16px !important; }
                .custom-table td { padding: 14px 16px !important; font-size: 14px; color: #334155; vertical-align: middle !important; }
                .action-btn { padding: 5px 10px; font-size: 12px; font-weight: 500; border-radius: 6px; }

                /* BUTTONS */
                .back-btn, .add-btn, .export-btn { border-radius: 10px; font-weight: 600; font-size: 14px; padding: 9px 18px; display: inline-flex; align-items: center; gap: 8px; box-shadow: 0 2px 6px rgba(0,0,0,0.04); }
                .add-btn { background: #059669; border-color: #059669; }
                .add-btn:hover { background: #047857; border-color: #047857; }

                /* FOOTER */
                .footer-section { background: white; border-top: 1px solid #e2e8f0; padding: 20px 0; }
                .footer-content { display: flex; align-items: center; justify-content: center; gap: 18px; text-align: left; }
                .footer-brand img { width: 145px; max-width: 100%; height: auto; }
                .footer-text p { margin: 0 0 3px; color: #64748b; font-size: 13px; }
                .footer-text p a { color: #2563eb; text-decoration: none; font-weight: 600; }
                .footer-text span { color: #94a3b8; font-size: 11px; }

                @media (max-width: 768px) {
                    .footer-content { flex-direction: column; gap: 8px; text-align: center; }
                }
            `}</style>
        </div>
    );
};

export default SesionFeedback;